import { EditOutlined, UserOutlined } from '@ant-design/icons';
import * as api from '@apis/admin-user';
import { Divider } from '@components/common/Divider';
import { UserCreateModal } from '@components/user/UserCreateModal';
import { UserEditModal } from '@components/user/UserEditModal';
import { Roles, ROLE_LABELS } from '@consts/role';
import { Permission, PERMISSION_GROUPS } from '@consts/permissions';
import { useAuthentication } from '@hooks/authentication';
import { AdminUser } from '@models/auth';
import { useAdminUserManagementStore } from '@stores/adminUserManagementStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Button, Checkbox, Input, Modal, Table, Tag } from 'antd';
import { filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

interface AdminUserWithPermissions extends AdminUser {
  authorizations?: Permission[];
}

const AdminUserPage: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const [editUserId, setEditUserId] = useState<string>();
  const [showUserCreate, setShowUserCreate] = useState<boolean>(false);
  const [showPermissionModal, setShowPermissionModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<AdminUserWithPermissions>();
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);

  useAuthentication();
  const store = useAdminUserManagementStore();
  const dialog = useOkCancelDialog();

  const userSearchQuery = useQuery({
    queryKey: [`admin-search-users`, JSON.stringify(store.searchQuery)],
    queryFn: () =>
      api.searchUsers({
        ...store.searchQuery,
        roles: [Roles.SUBADMIN, Roles.ADMIN],
      }),
    enabled: true,
  });

  const updateUserPermissionsMutation = useMutation({
    mutationFn: (data: { userId: string; authorizations: Permission[] }) => {
      const formData = new FormData();
      formData.append('userId', data.userId);
      formData.append('json', JSON.stringify({ authorizations: data.authorizations }));
      return api.updateUser(formData);
    },
    onSuccess: () => {
      toast.success('사용자 권한이 업데이트되었습니다.');
      userSearchQuery.refetch();
      setShowPermissionModal(false);
    },
    onError: (error) => {
      toast.error('사용자 권한 업데이트에 실패했습니다.');
      console.error(error);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: api.deleteUser,
    onSuccess: () => {
      userSearchQuery.refetch();
      toast.dark('탈퇴 처리 되었습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
      });
    },
  });

  const restoreDeletedUserMutation = useMutation({
    mutationFn: api.restoreDeletedUser,
    onSuccess: () => {
      toast.dark('탈퇴 복구 되었습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
      });
      userSearchQuery.refetch();
    },
  });

  useEffect(() => {
    if (userSearchQuery.isSuccess) {
      store.setUsers(userSearchQuery.data?.data?.contents || []);
      store.setUsersContainer({
        totalCount: userSearchQuery.data?.data?.total || 0,
      });
    }
  }, [userSearchQuery.data]);

  const handleSearch = () => {
    store.setSearchQuery({
      ...store.searchQuery,
      keyword: searchKeyword,
      page: 0,
    });
  };

  const handleEditUser = (userId: string) => {
    setEditUserId(userId);
  };

  const handleEditPermissions = (user: AdminUserWithPermissions) => {
    setSelectedUser(user);
    setUserPermissions(user.authorizations || []);
    setShowPermissionModal(true);
  };

  const handlePermissionChange = (permission: Permission, checked: boolean) => {
    if (checked) {
      setUserPermissions([...userPermissions, permission]);
    } else {
      setUserPermissions(userPermissions.filter((p) => p !== permission));
    }
  };

  const handleGroupPermissionChange = (groupPermissions: Permission[], checked: boolean) => {
    if (checked) {
      const newPermissions = [...userPermissions];
      groupPermissions.forEach((permission) => {
        if (!newPermissions.includes(permission)) {
          newPermissions.push(permission);
        }
      });
      setUserPermissions(newPermissions);
    } else {
      setUserPermissions(userPermissions.filter((p) => !groupPermissions.includes(p)));
    }
  };

  const isGroupChecked = (groupPermissions: Permission[]) => {
    return groupPermissions.every((permission) => userPermissions.includes(permission));
  };

  const isGroupIndeterminate = (groupPermissions: Permission[]) => {
    const checkedCount = groupPermissions.filter((permission) => userPermissions.includes(permission)).length;
    return checkedCount > 0 && checkedCount < groupPermissions.length;
  };

  const columns: ColumnsType<AdminUserWithPermissions> = [
    {
      title: 'ID 복사',
      align: 'center',
      width: '100px',
      dataIndex: '_id',
      render: (_id) => (
        <Button
          onClick={() => {
            navigator.clipboard.writeText(_id);
            toast.dark('사용자 ID를 클립보드에 복사했습니다', {
              position: 'top-center',
              autoClose: 3000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              pauseOnFocusLoss: false,
              draggable: true,
              progress: undefined,
              bodyStyle: {
                color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                textAlign: 'center',
                fontFamily: 'Pretendard',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '20px',
              },
              theme: 'dark',
            });
          }}>
          ID 복사
        </Button>
      ),
    },
    {
      title: '이메일',
      dataIndex: 'emails',
      key: 'emails',
      width: 200,
      render: (emails: { address: string; verified?: boolean }[]) => {
        const primaryEmail = emails.find((email) => email.verified) || emails[0];
        return (
          <div>
            <div>{primaryEmail?.address}</div>
            {primaryEmail?.verified && <Badge status="success" text="인증됨" />}
          </div>
        );
      },
    },
    {
      title: '이름',
      dataIndex: 'profile',
      key: 'profile',
      width: 150,
      render: (profile) => (
        <div>
          {profile?.nickname && <div>{profile.nickname}</div>}
          <div>{profile?.name || '-'}</div>
        </div>
      ),
    },
    {
      title: '전화번호',
      dataIndex: 'profile',
      key: 'phone',
      width: 136,
      render: (profile) => {
        if (!profile?.phone) {
          return '-';
        }
        return (
          <Button
            onClick={() => {
              navigator.clipboard.writeText(profile.phone);
              toast.info('전화번호를 클립보드에 복사했습니다.', {
                position: 'top-center',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                pauseOnFocusLoss: false,
                draggable: true,
                progress: undefined,
                theme: 'light',
              });
            }}>
            {profile.phone}
          </Button>
        );
      },
    },
    {
      title: '역할',
      dataIndex: 'roles',
      key: 'roles',
      width: 150,
      render: (roles: Roles[]) => (
        <div>
          {roles.map((role) => (
            <Tag key={role} color="blue">
              {ROLE_LABELS[role]}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: '권한',
      dataIndex: 'authorizations',
      key: 'authorizations',
      width: 200,
      render: (authorizations: Permission[]) => {
        if (!authorizations || authorizations.length === 0) {
          return <Tag color="red">권한 없음</Tag>;
        }
        if (authorizations.includes('*')) {
          return <Tag color="gold">모든 권한</Tag>;
        }
        return (
          <div>
            {authorizations.slice(0, 3).map((permission) => (
              <Tag key={permission} color="green" style={{ marginBottom: 2 }}>
                {permission}
              </Tag>
            ))}
            {authorizations.length > 3 && <Tag color="default">+{authorizations.length - 3}</Tag>}
          </div>
        );
      },
    },
    {
      title: '생성일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: Date) => moment(date).format('YYYY-MM-DD'),
    },
    {
      title: '액션',
      key: 'action',
      width: 200,
      align: 'center',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEditUser(record._id)}>
              수정
            </Button>
            <Button type="link" icon={<UserOutlined />} onClick={() => handleEditPermissions(record)}>
              권한
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: '탈퇴',
      key: 'delete_user',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
          {record.deletedAt && (
            <Button
              type="dashed"
              onClick={() => {
                dialog.open({
                  title: '탈퇴 복구',
                  content: '탈퇴 유저를 복구하시겠습니까?',
                  type: 'ok_cancel',
                  onConfirm: () => {
                    restoreDeletedUserMutation.mutate(record._id);
                  },
                });
              }}>
              탈퇴 복구
            </Button>
          )}
          {!record.deletedAt && (
            <Button
              danger
              onClick={() => {
                dialog.open({
                  title: '탈퇴 처리',
                  content: '유저를 탈퇴 처리 하시겠습니까?\n탈퇴 처리한 유저는 30일 이후 완전히 삭제됩니다.',
                  type: 'ok_cancel',
                  onConfirm: () => {
                    deleteUserMutation.mutate(record._id);
                  },
                });
              }}>
              탈퇴 처리
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <h2>어드민 유저 관리</h2>

      {/* 검색 조건 */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', alignItems: 'center' }}>
        <Input
          className={filterActiveClassName(!!searchKeyword)}
          placeholder="이메일로 검색"
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 300 }}
        />
        <Button type="primary" onClick={handleSearch}>
          검색
        </Button>
        <Button type="default" onClick={() => setShowUserCreate(true)}>
          유저 생성
        </Button>
      </div>

      {/* 데이터 테이블 */}
      <Table
        columns={columns}
        dataSource={store.users}
        rowKey="_id"
        loading={userSearchQuery.isLoading}
        pagination={{
          current: store.searchQuery.page + 1,
          pageSize: store.searchQuery.size,
          total: store.usersContainer?.totalCount || 0,
          onChange: (page) => {
            store.setSearchQuery({
              ...store.searchQuery,
              page: page - 1,
            });
          },
        }}
        scroll={{ x: 1100 }}
      />

      {/* 유저 생성 모달 */}
      {showUserCreate && (
        <UserCreateModal
          open={showUserCreate}
          onClose={() => setShowUserCreate(false)}
          needRefetch={() => {
            setShowUserCreate(false);
            userSearchQuery.refetch();
          }}
        />
      )}

      {/* 유저 수정 모달 */}
      {editUserId && (
        <UserEditModal
          userId={editUserId}
          open={!!editUserId}
          onClose={() => setEditUserId(undefined)}
          needRefetch={() => {
            setEditUserId(undefined);
            userSearchQuery.refetch();
          }}
        />
      )}

      {/* 권한 설정 모달 */}
      <Modal
        title={`${selectedUser?.emails?.[0]?.address} 권한 설정`}
        open={showPermissionModal}
        onCancel={() => setShowPermissionModal(false)}
        onOk={() => {
          if (selectedUser) {
            updateUserPermissionsMutation.mutate({
              userId: selectedUser._id,
              authorizations: userPermissions,
            });
          }
        }}
        width={800}
        confirmLoading={updateUserPermissionsMutation.isPending}>
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {/* 모든 권한 */}
          <div style={{ marginBottom: '16px' }}>
            <Checkbox
              checked={userPermissions.includes('*')}
              onChange={(e) => {
                if (e.target.checked) {
                  setUserPermissions(['*']);
                } else {
                  setUserPermissions([]);
                }
              }}>
              <strong>모든 권한 (*)</strong>
            </Checkbox>
          </div>

          <Divider />

          {/* 권한 그룹별 설정 */}
          {Object.entries(PERMISSION_GROUPS).map(([groupName, permissions]) => (
            <div key={groupName} style={{ marginBottom: '16px' }}>
              <div style={{ marginBottom: '8px' }}>
                <Checkbox
                  checked={isGroupChecked(permissions)}
                  indeterminate={isGroupIndeterminate(permissions)}
                  onChange={(e) => handleGroupPermissionChange(permissions, e.target.checked)}>
                  <strong>{groupName}</strong>
                </Checkbox>
              </div>
              <div style={{ marginLeft: '24px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {permissions.map((permission) => (
                  <Checkbox
                    key={permission}
                    checked={userPermissions.includes(permission)}
                    onChange={(e) => handlePermissionChange(permission, e.target.checked)}
                    disabled={userPermissions.includes('*')}>
                    {permission}
                  </Checkbox>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default AdminUserPage;
